import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { Observable, filter, first, lastValueFrom, Subscription, combineLatest, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { switchMap, startWith } from 'rxjs/operators';
import {
  CLIENTPORTAL,
  COMMUNITY,
  COMPANY,
  DASHBOARD,
  MENTORDATABASE,
  PROFILE,
  RELATIONS,
  REPORTS,
  RESOURCES,
  TASKTRACKER,
  CAREERPLANNER,
  KICKOFF,
  WORKSHOPS,
  NETWORKING,
  MENTORRATING,
} from 'src/app/app-routing.module';
import {
  FBKSAWAITING,
  FINALOVERVIEW,
  FINALRATING,
  LSWORKSHOPS,
  LSCOACHING,
  MATCHES,
  OVERALLRATING,
  PROGRESSRATING,
} from 'src/app/reports/reports.module';
import { MenteeRelation } from 'src/app/shared/types/mentee-relations.types';
import { hasValue, isNetworkingRoundApproaching } from 'src/app/shared/utils';
import { AppState } from 'src/app/state/reducers';
import { selectLastMentee } from 'src/app/state/selectors/mentee.selector';
import { BannerType } from '../head-banner/head-banner.component';
import { LanguageService } from 'src/app/shared/services/language.service';
import { selectMrs } from 'src/app/state/selectors/mentee-relations.selector';
import { Router } from '@angular/router';

@Component({
  selector: 'fp-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {
  private static readonly MATCH_MODAL_SEEN_KEY = 'match_modal_seen';
  public currentUser: any;
  private currentMrs: any;
  private isCzech = false;
  private subscriptions = new Subscription();
  private suppressMatchModalForSession = false;

  constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private languageService: LanguageService,
    private router: Router
  ) {}
  readonly isCzech$ = this.languageService.isCzech$;
  showMatchModal: boolean = false;
  matchedRelation: MenteeRelation | null = null;
  matchModalItems: MenteeRelation[] = [];
  matchModalIndex: number = 0;
  bannerTypes: BannerType[] = [];
  dismissedBannerTypes: BannerType[] = [];
  isMentorContacted = false;
  mrs$: Observable<MenteeRelation[]> = this.store.select(state => state.menteeRelations);
  role$: Observable<any>;
  role: any;
  visibleSidebar: boolean;
  items: MenuItem[];
  readonly RELATIONS = RELATIONS + '/';
  user$ = this.store.select(state => state.settings.user);
  reportsMenu = {
    label: 'Reports',
    items: [
      { label: 'Mentee-mentor matches', routerLink: REPORTS + '/' + MATCHES },
      { label: 'Overall rating', routerLink: REPORTS + '/' + OVERALLRATING },
      { label: 'Mentee rating - Progress', routerLink: REPORTS + '/' + PROGRESSRATING },
      { label: 'Mentee rating - Final', routerLink: REPORTS + '/' + FINALRATING },
      { label: 'Final overview', routerLink: REPORTS + '/' + FINALOVERVIEW },
      { label: 'Feedbacks awaiting', routerLink: REPORTS + '/' + FBKSAWAITING },
    ],
    expanded: false,
  };
  isWorkshopOnlyAttendee = false;

  ngOnInit() {
    const czechBannerSubscription = this.isCzech$.subscribe(isCzech => {
      this.isCzech = isCzech;
      this.syncCzechBanner();
    });
    this.subscriptions.add(czechBannerSubscription);

    // Combine user$ with language changes to trigger menu rebuild
    const userAndLang$ = combineLatest([this.user$.pipe(filter(user => !!user)), this.translate.onLangChange.pipe(startWith(null))]);

    const mainSubscription = userAndLang$
      .pipe(
        switchMap(([user]) => {
          this.currentUser = user;
          this.syncCzechBanner();
          this.syncNetworkingRoundBanner();
          // For ME/MR/CH roles, combine with mrs$ to build menu
          if (['ME', 'MR', 'CH'].includes(user?.role)) {
            return combineLatest([of(user), this.mrs$]).pipe(switchMap(([usr, mrs]) => this.handleUserAndBuildMenu(usr, mrs)));
          } else {
            // For other roles, just handle user
            return this.handleUserAndBuildMenu(user, null);
          }
        })
      )
      .subscribe();

    this.subscriptions.add(mainSubscription);
  }

  private handleUserAndBuildMenu(user: any, mrs: MenteeRelation[] | null): Observable<void> {
    return new Observable(observer => {
      (async () => {
        try {
          await this.handleUserRole(user);
          this.isWorkshopOnlyAttendee = this.role?.workshops_only_attendee;

          if (mrs !== null) {
            this.currentMrs = mrs;
            this.items = this.buildMenuItems(user, mrs);
          }

          observer.next();
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      })();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  private async handleUserRole(user: any) {
    this.bannerTypes = this.bannerTypes.filter(type => type === 'czMaterialsEn' || type === 'networkingRound');

    if (user?.role === 'ME') {
      await this.setRole(selectLastMentee());
    } else if (user?.role === 'MR') {
      await this.setRole(state => state.mentor);
      this.isMentorContacted = this.role.contacted_for_mentee;
      this.cdr.detectChanges();
      this.setBannerForMentor();
    } else if (user?.role === 'CH') {
      await this.setRole(state => state.coach);
      if (this.role?.status === 18) {
        this.addBanner('offboardedCoach');
      }
    }

    // Match modal logic (single localStorage key, supports multiple modals)
    if (['ME', 'MR', 'CH'].includes(user?.role)) {
      const mrs = await lastValueFrom(this.store.select(selectMrs).pipe(filter(hasValue), first()));
      this.matchModalItems = this.getUnseenMatchedRelations(mrs ?? [], user?.role, user?.id);
      this.matchModalIndex = 0;
      this.showNextMatchModalIfAny();
    }

    this.setBannerForNewUser(user);
    if (user?.role === 'LD') {
      this.items = [{ label: this.translate.instant('MENU.CAREER_PLANNER'), routerLink: CAREERPLANNER }];
    } else if (user?.role === 'CC') {
      this.translate.use('en');
      await this.setRole(state => state.company);
      this.addCompanyReportTabs();
      this.items = [
        { label: 'Dashboard', routerLink: COMPANY },
        this.reportsMenu,
        { label: 'Client Portal', routerLink: CLIENTPORTAL },
        {
          label: 'Employer Branding',
          items: [
            { label: 'Client spotlight', routerLink: 'client-spotlight' },
            { label: 'Job listings', routerLink: 'job-listings' },
            { label: 'Event partnerships', routerLink: 'event-partnerships' },
          ],
          expanded: true,
        },
      ];
    }
  }

  private async setRole(selector: any) {
    this.role$ = this.store.select(selector).pipe(filter(hasValue), first());
    this.role = await lastValueFrom(this.role$);
  }

  private setBannerForMentor() {
    if (this.role?.status === 46) {
      this.addBanner('offboarded');
    } else if (this.role?.status === 19) {
      this.addBanner('notresponsive');
    }
  }

  private setBannerForNewUser(user: any) {
    const newUserCutoff = new Date();
    newUserCutoff.setDate(newUserCutoff.getDate() - 7);
    if (this.role?.user_data?.date_joined > newUserCutoff && user?.role !== 'CC') {
      this.addBanner(user?.role === 'CH' ? 'tutorial-coach' : 'tutorial');
    }
  }

  private addCompanyReportTabs() {
    const workshopsRatingLabel = 'Workshops rating';
    const coachingRatingLabel = 'Coaching rating';
    if (this.role?.workshops && !this.reportsMenu.items.some(item => item.label === workshopsRatingLabel)) {
      this.reportsMenu.items.push({ label: workshopsRatingLabel, routerLink: REPORTS + '/' + LSWORKSHOPS });
    }
    if (this.role?.coaching && !this.reportsMenu.items.some(item => item.label === coachingRatingLabel)) {
      this.reportsMenu.items.push({ label: coachingRatingLabel, routerLink: REPORTS + '/' + LSCOACHING });
    }
  }

  private buildMenuItems(user: any, mrs: MenteeRelation[]): MenuItem[] {
    const mentoring_items = this.filterRelations(mrs, 10, 'mentor', user);
    const finished_mentoring_items = this.filterFinishedRelations(mrs, 10, 'mentor', user);
    const coaching_items = this.filterRelations(mrs, 11, 'coach', user);
    const finished_coaching_items = this.filterFinishedRelations(mrs, 11, 'coach', user);

    const taskTrackerItem =
      user?.role === 'ME' ? [{ label: this.translate.instant('MENU.TASKS_TRACKER'), routerLink: `/${TASKTRACKER}` }] : [];
    const kickoffItem = user?.role === 'ME' ? [{ label: this.translate.instant('MENU.KICKOFF_RECORDING'), routerLink: `/${KICKOFF}` }] : [];

    return [
      ...(user?.role !== 'CH' && !this.isWorkshopOnlyAttendee
        ? [{ label: this.translate.instant('MENU.DASHBOARD'), routerLink: DASHBOARD }]
        : []),
      ...(this.shouldShowMentoringTab(mentoring_items, finished_mentoring_items, coaching_items, finished_coaching_items, user)
        ? [{ label: this.translate.instant('MENU.MENTORING'), routerLink: this.RELATIONS }]
        : []),
      ...(mentoring_items.length && !this.isWorkshopOnlyAttendee
        ? [
            {
              label: this.translate.instant('MENU.MENTORING'),
              items: [...mentoring_items, ...taskTrackerItem, ...kickoffItem],
              expanded: true,
            },
          ]
        : []),
      ...(coaching_items.length && !this.isWorkshopOnlyAttendee
        ? [{ label: this.translate.instant('MENU.COACHING'), items: coaching_items, expanded: true }]
        : []),
      ...(finished_mentoring_items.length && mentoring_items.length && !this.isWorkshopOnlyAttendee
        ? [{ label: this.translate.instant('MENU.FINISHED_MENTORING'), items: finished_mentoring_items, expanded: false }]
        : []),
      ...(finished_mentoring_items.length && !mentoring_items.length && !this.isWorkshopOnlyAttendee
        ? [
            {
              label: this.translate.instant('MENU.FINISHED_MENTORING'),
              items: [...finished_mentoring_items, ...taskTrackerItem],
              expanded: false,
            },
          ]
        : []),
      ...(finished_coaching_items.length && !this.isWorkshopOnlyAttendee
        ? [{ label: this.translate.instant('MENU.FINISHED_COACHING'), items: finished_coaching_items, expanded: false }]
        : []),
      ...(user?.role === 'ME' && this.role?.workshops?.length > 0
        ? [{ label: this.translate.instant('MENU.WORKSHOPS'), routerLink: [WORKSHOPS] }]
        : []),
      ...(user?.role === 'ME'
        ? [
            { label: this.translate.instant('MENU.COMMUNITY'), routerLink: COMMUNITY },
            { label: this.translate.instant('MENU.NETWORKING'), routerLink: NETWORKING },
          ]
        : []),
      ...(user?.role === 'MR' ? [{ label: this.translate.instant('MENU.YOUR_PERFORMANCE'), routerLink: MENTORRATING }] : []),
      { label: this.translate.instant('MENU.RESOURCES'), routerLink: RESOURCES },
      ...(user?.role === 'MR'
        ? [
            {
              label: this.translate.instant('MENU.NETWORKING'),
              items: [
                { label: this.translate.instant('MENU.NETWORKING_1_1'), routerLink: NETWORKING },
                { label: this.translate.instant('MENU.MENTOR_DATABASE'), routerLink: MENTORDATABASE },
              ],
              expanded: true,
            },
            { label: this.translate.instant('MENU.MY_MENTOR_PROFILE'), routerLink: PROFILE },
          ]
        : []),
      ...(user?.role === 'CH' ? [{ label: this.translate.instant('MENU.MY_COACH_PROFILE'), routerLink: PROFILE }] : []),
      { label: this.translate.instant('MENU.CAREER_PLANNER'), routerLink: CAREERPLANNER },
    ];
  }

  private filterRelations(mrs: MenteeRelation[], status: number, type: string, user: any): MenuItem[] {
    return (
      mrs
        ?.filter(rel => rel.mr_status === status && rel.supporter.type === type)
        .map(rel => ({
          label:
            user?.role === 'ME'
              ? `${rel.supporter.first_name} ${rel.supporter.last_name[0]}.`
              : `${rel.mentee.first_name} ${rel.mentee.last_name[0]}.`,
          routerLink: [this.RELATIONS + rel.id],
        })) ?? []
    );
  }

  private filterFinishedRelations(mrs: MenteeRelation[], status: number, type: string, user: any): MenuItem[] {
    return (
      mrs
        ?.filter(rel => rel.mr_status !== status && rel.supporter.type === type)
        .map(rel => ({
          label:
            user?.role === 'ME'
              ? `${rel.supporter.first_name} ${rel.supporter.last_name[0]}.`
              : `${rel.mentee.first_name} ${rel.mentee.last_name[0]}.`,
          routerLink: [this.RELATIONS + rel.id],
        })) ?? []
    );
  }

  private shouldShowMentoringTab(
    mentoring_items: MenuItem[],
    finished_mentoring_items: MenuItem[],
    coaching_items: MenuItem[],
    finished_coaching_items: MenuItem[],
    user: any
  ): boolean {
    return (
      mentoring_items.length === 0 &&
      finished_mentoring_items.length === 0 &&
      coaching_items.length === 0 &&
      finished_coaching_items.length === 0 &&
      user?.role === 'ME' &&
      this.role?.programme == 5
    );
  }

  closeBanner(type: BannerType) {
    this.removeBanner(type);
    if (!this.dismissedBannerTypes.includes(type)) {
      this.dismissedBannerTypes.push(type);
    }
  }

  isAnyBannerVisible(): boolean {
    return this.bannerTypes.length > 0;
  }

  private addBanner(type: BannerType) {
    if (this.dismissedBannerTypes.includes(type) || this.bannerTypes.includes(type)) {
      return;
    }
    this.bannerTypes.push(type);
  }

  private removeBanner(type: BannerType) {
    this.bannerTypes = this.bannerTypes.filter(bannerType => bannerType !== type);
  }

  private syncCzechBanner() {
    const shouldShowCzechBanner = this.isCzech && this.currentUser?.role === 'MR';

    if (shouldShowCzechBanner) {
      this.addBanner('czMaterialsEn');
      return;
    }

    this.removeBanner('czMaterialsEn');
  }

  private syncNetworkingRoundBanner() {
    const isNetworkingRole = ['ME', 'MR'].includes(this.currentUser?.role);
    const shouldShowBanner = isNetworkingRole && isNetworkingRoundApproaching();
    if (shouldShowBanner) {
      this.addBanner('networkingRound');
      return;
    }

    this.removeBanner('networkingRound');
  }

  onModalClosed(): void {
    if (this.matchedRelation) {
      this.markMatchModalSeen(this.matchedRelation, this.currentUser?.id);
    }
    this.onModalNextClicked();
  }

  onModalCtaClicked(): void {
    if (!this.matchedRelation) return;

    const relationId = this.matchedRelation.id;
    this.markMatchModalSeen(this.matchedRelation, this.currentUser?.id);

    // Close modal and navigate. We intentionally don't immediately show the next modal
    // so it doesn't pop up on top of the destination page.
    this.suppressMatchModalForSession = true;
    this.showMatchModal = false;
    this.router.navigate([RELATIONS, relationId]);
  }

  onModalNextClicked(): void {
    if (!this.matchedRelation) return;

    if (this.matchModalIndex + 1 < this.matchModalItems.length) {
      this.matchModalIndex += 1;
      this.matchedRelation = this.matchModalItems[this.matchModalIndex];
      this.showMatchModal = true;
      this.cdr.detectChanges();
      return;
    }

    this.matchModalItems = [];
    this.matchModalIndex = 0;
    this.matchedRelation = null;
    this.showMatchModal = false;
  }

  private showNextMatchModalIfAny(): void {
    if (this.suppressMatchModalForSession) {
      this.matchedRelation = null;
      this.showMatchModal = false;
      return;
    }

    if (this.matchModalItems.length > 0) {
      this.matchedRelation = this.matchModalItems[this.matchModalIndex] ?? this.matchModalItems[0];
      this.showMatchModal = true;
      this.cdr.detectChanges();
      return;
    }
    this.matchedRelation = null;
    this.showMatchModal = false;
  }

  private getUnseenMatchedRelations(mrs: MenteeRelation[], userRole: string, userId?: string | number): MenteeRelation[] {
    const seenMap = this.getMatchModalSeenMap(userId);
    const previousLastLoginDate = this.getPreviousLastLoginDate(userId);
    const relevant = (mrs ?? []).filter(mr => {
      if (!mr) return false;
      if (!(mr.mr_status === 10 || mr.mr_status === 11)) return false;

      // Role-specific relevance
      if (userRole === 'CH' && mr?.supporter?.type !== 'coach') return false;
      if (userRole === 'MR' && mr?.supporter?.type !== 'mentor') return false;
      // For ME we allow both mentor and coach matches (mentee can be in mentoring/coaching).
      if (userRole === 'ME' && !['mentor', 'coach'].includes(mr?.supporter?.type as any)) return false;

      const matchedDate = new Date(mr.matched_date);
      if (previousLastLoginDate && matchedDate <= previousLastLoginDate) return false;

      const seenMatchedDate = seenMap[mr.id] ? new Date(seenMap[mr.id]) : null;
      return !seenMatchedDate || seenMatchedDate < matchedDate;
    });

    return relevant.sort((a, b) => new Date(b.matched_date).getTime() - new Date(a.matched_date).getTime());
  }

  /**
   * Storage format (single key):
   * {
   *   "<userId>": { "<relationId>": "<matched_date>", ... },
   *   ...
   * }
   *
   * Backwards compatible with the legacy flat map:
   * { "<relationId>": "<matched_date>", ... }
   */
  private getMatchModalSeenMap(userId?: string | number): Record<string, string> {
    try {
      const raw = localStorage.getItem(LayoutComponent.MATCH_MODAL_SEEN_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};

      const uid = userId != null ? String(userId) : null;

      // v3 user bucket
      if (uid && (parsed as any)[uid] && typeof (parsed as any)[uid] === 'object') {
        const bucket = (parsed as any)[uid];
        if (bucket && typeof bucket === 'object' && 'seen' in bucket) {
          return bucket.seen && typeof bucket.seen === 'object' ? (bucket.seen as Record<string, string>) : {};
        }
      }

      // Legacy flat map (values are strings)
      const values = Object.values(parsed as Record<string, unknown>);
      const looksLikeLegacy = values.length === 0 || values.every(v => typeof v === 'string');
      if (looksLikeLegacy) return parsed as Record<string, string>;

      if (!uid) return {};
      const byUser = (parsed as Record<string, unknown>)[uid];
      if (!byUser || typeof byUser !== 'object') return {};
      // v2 per-user flat map
      return byUser as Record<string, string>;
    } catch {
      return {};
    }
  }

  private getPreviousLastLoginDate(userId?: string | number): Date | null {
    if (userId == null) return null;

    try {
      const raw = localStorage.getItem(LayoutComponent.MATCH_MODAL_SEEN_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;

      const uid = String(userId);
      const bucket = (parsed as any)[uid];
      const prev = bucket?.previous_last_login;
      if (!prev) return null;

      const d = new Date(prev);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  private markMatchModalSeen(relation: MenteeRelation, userId?: string | number): void {
    const uid = userId != null ? String(userId) : null;

    // If we can't resolve userId, fall back to legacy flat map.
    if (!uid) {
      const legacy = this.getMatchModalSeenMap(undefined);
      legacy[relation.id] = relation.matched_date;
      localStorage.setItem(LayoutComponent.MATCH_MODAL_SEEN_KEY, JSON.stringify(legacy));
      return;
    }

    let root: Record<string, any> = {};
    try {
      const raw = localStorage.getItem(LayoutComponent.MATCH_MODAL_SEEN_KEY);
      root = raw ? JSON.parse(raw) : {};
      if (!root || typeof root !== 'object') root = {};
    } catch {
      root = {};
    }

    // v3: ensure bucket + seen map
    if (root[uid] && typeof root[uid] === 'object' && 'seen' in root[uid]) {
      if (!root[uid].seen || typeof root[uid].seen !== 'object') root[uid].seen = {};
      root[uid].seen[relation.id] = relation.matched_date;
      localStorage.setItem(LayoutComponent.MATCH_MODAL_SEEN_KEY, JSON.stringify(root));
      return;
    }

    // Migrate legacy flat map -> per-user map
    const values = Object.values(root);
    const looksLikeLegacy = values.length === 0 || values.every(v => typeof v === 'string');
    if (looksLikeLegacy) {
      const legacy = root as Record<string, string>;
      root = { [uid]: { seen: legacy } };
    }

    // Migrate v2 per-user flat -> v3 user bucket
    if (root[uid] && typeof root[uid] === 'object' && !('seen' in root[uid])) {
      const flat = root[uid];
      root[uid] = { seen: flat };
    }

    if (!root[uid] || typeof root[uid] !== 'object') root[uid] = { seen: {} };
    if (!root[uid].seen || typeof root[uid].seen !== 'object') root[uid].seen = {};
    root[uid].seen[relation.id] = relation.matched_date;

    localStorage.setItem(LayoutComponent.MATCH_MODAL_SEEN_KEY, JSON.stringify(root));
  }

  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;
  showScrollTop = false;

  onContentScroll(event: Event): void {
    const threshold = window.innerWidth >= 1200 ? 300 : 500;
    this.showScrollTop = (event.target as HTMLElement).scrollTop > threshold;
  }

  scrollToTop(): void {
    this.scrollContainer?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  hideSidebar() {
    this.visibleSidebar = false;
  }
}
